import React, { useState, useMemo } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  ButtonGroup,
  Select,
  useColorModeValue,
  Center,
  Icon,
  Badge,
  useBreakpointValue,
} from '@chakra-ui/react';
import { FiCheckCircle, FiFilter } from 'react-icons/fi';
import TaskCard from './TaskCard';
import moment from 'moment';

export default function TaskList({ 
  tasks = [], 
  onComplete, 
  onReopen,
  onEdit, 
  onDelete,
  onClick,
  showFilters = true,
  getUserName,
  showAssignee = false
}) {
  const [statusFilter, setStatusFilter] = useState('active'); // active, completed, all
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [groupBy, setGroupBy] = useState('date'); // date, priority, type

  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  // Defaults to the md value so desktop never flashes the unattached variant.
  const isAttachedFilters = useBreakpointValue({ base: false, md: true }, 'md');

  // Filter tasks
  const filteredTasks = useMemo(() => {
    let result = [...tasks];

    // Status filter
    if (statusFilter === 'active') {
      result = result.filter(t => t.status !== 'completed' && t.status !== 'cancelled');
    } else if (statusFilter === 'completed') {
      result = result.filter(t => t.status === 'completed');
    }

    // Priority filter
    if (priorityFilter !== 'all') {
      result = result.filter(t => t.priority === priorityFilter);
    }

    return result;
  }, [tasks, statusFilter, priorityFilter]);

  // Group tasks
  const groupedTasks = useMemo(() => {
    if (groupBy === 'date') {
      const groups = {
        overdue: [],
        today: [],
        thisWeek: [],
        later: [],
      };

      filteredTasks.forEach(task => {
        if (!task.dueDate) {
          groups.later.push(task);
          return;
        }

        const dueDate = moment(task.dueDate.toDate ? task.dueDate.toDate() : task.dueDate);
        const now = moment();
        const endOfWeek = moment().endOf('week');

        if (dueDate.isBefore(now, 'day') && task.status !== 'completed') {
          groups.overdue.push(task);
        } else if (dueDate.isSame(now, 'day')) {
          groups.today.push(task);
        } else if (dueDate.isBefore(endOfWeek, 'day')) {
          groups.thisWeek.push(task);
        } else {
          groups.later.push(task);
        }
      });

      return groups;
    } else if (groupBy === 'priority') {
      return {
        urgent: filteredTasks.filter(t => t.priority === 'urgent'),
        high: filteredTasks.filter(t => t.priority === 'high'),
        medium: filteredTasks.filter(t => t.priority === 'medium'),
        low: filteredTasks.filter(t => t.priority === 'low'),
      };
    } else if (groupBy === 'type') {
      return {
        crm: filteredTasks.filter(t => t.type === 'crm'),
        loan: filteredTasks.filter(t => t.type === 'loan'),
        marketing: filteredTasks.filter(t => t.type === 'marketing'),
        custom: filteredTasks.filter(t => t.type === 'custom' || !t.type),
      };
    }
    return {};
  }, [filteredTasks, groupBy]);

  if (tasks.length === 0) {
    return (
      <Center py={12}>
        <VStack>
          <Icon as={FiCheckCircle} boxSize={12} color="green.300" />
          <Text color="gray.600" fontSize="lg" fontWeight="medium">
            No tasks yet
          </Text>
          <Text fontSize="sm" color="gray.500">
            Create your first task to get started
          </Text>
        </VStack>
      </Center>
    );
  }

  const renderGroup = (title, tasks, colorScheme = 'gray') => {
    if (tasks.length === 0) return null;

    return (
      <Box key={title}>
        <HStack mb={3}>
          <Heading size="sm" color="gray.700">
            {title}
          </Heading>
          <Badge colorScheme={colorScheme}>{tasks.length}</Badge>
        </HStack>
        <VStack align="stretch" spacing={2}>
          {tasks.map(task => (
            <TaskCard
              key={task.id}
              task={task}
              onComplete={onComplete}
              onReopen={onReopen}
              onEdit={onEdit}
              onDelete={onDelete}
              onClick={onClick}
              getUserName={getUserName}
              showAssignee={showAssignee}
            />
          ))}
        </VStack>
      </Box>
    );
  };

  return (
    <VStack align="stretch" spacing={6}>
      {showFilters && (
        <Box bg={bg} borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={4}>
          <HStack spacing={{ base: 2, md: 4 }} flexWrap="wrap" gap={2}>
            <HStack spacing={2}>
              <Icon as={FiFilter} color="gray.600" />
              <Text fontSize="sm" fontWeight="semibold">Filters:</Text>
            </HStack>

            {/* isAttached makes these three a single rigid unit, so it's dropped on
                narrow screens to let them wrap individually. */}
            <ButtonGroup size="sm" isAttached={isAttachedFilters} flexWrap="wrap" gap={isAttachedFilters ? 0 : 1}>
              <Button
                variant={statusFilter === 'active' ? 'solid' : 'outline'}
                colorScheme={statusFilter === 'active' ? 'blue' : 'gray'}
                onClick={() => setStatusFilter('active')}
              >
                Active
              </Button>
              <Button
                variant={statusFilter === 'completed' ? 'solid' : 'outline'}
                colorScheme={statusFilter === 'completed' ? 'blue' : 'gray'}
                onClick={() => setStatusFilter('completed')}
              >
                Completed
              </Button>
              <Button
                variant={statusFilter === 'all' ? 'solid' : 'outline'}
                colorScheme={statusFilter === 'all' ? 'blue' : 'gray'}
                onClick={() => setStatusFilter('all')}
              >
                All
              </Button>
            </ButtonGroup>

            <Select
              size="sm"
              maxW="150px"
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
            >
              <option value="all">All Priorities</option>
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </Select>

            <Select
              size="sm"
              maxW="150px"
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
            >
              <option value="date">Group by Date</option>
              <option value="priority">Group by Priority</option>
              <option value="type">Group by Type</option>
            </Select>
          </HStack>
        </Box>
      )}

      <VStack align="stretch" spacing={6}>
        {groupBy === 'date' && (
          <>
            {renderGroup('Overdue', groupedTasks.overdue, 'red')}
            {renderGroup('Today', groupedTasks.today, 'orange')}
            {renderGroup('This Week', groupedTasks.thisWeek, 'blue')}
            {renderGroup('Later', groupedTasks.later, 'gray')}
          </>
        )}
        
        {groupBy === 'priority' && (
          <>
            {renderGroup('Urgent', groupedTasks.urgent, 'red')}
            {renderGroup('High Priority', groupedTasks.high, 'orange')}
            {renderGroup('Medium Priority', groupedTasks.medium, 'yellow')}
            {renderGroup('Low Priority', groupedTasks.low, 'gray')}
          </>
        )}

        {groupBy === 'type' && (
          <>
            {renderGroup('CRM Tasks', groupedTasks.crm, 'blue')}
            {renderGroup('Loan Tasks', groupedTasks.loan, 'green')}
            {renderGroup('Marketing Tasks', groupedTasks.marketing, 'purple')}
            {renderGroup('Custom Tasks', groupedTasks.custom, 'gray')}
          </>
        )}
      </VStack>

      {filteredTasks.length === 0 && (
        <Center py={8}>
          <VStack>
            <Icon as={FiCheckCircle} boxSize={10} color="gray.300" />
            <Text color="gray.500" fontSize="sm">
              No tasks match your filters
            </Text>
          </VStack>
        </Center>
      )}
    </VStack>
  );
}

